/**
 * iam API namespace — auth + account. Shapes are typed from the observed v4
 * responses (`/auth/login` → {userId, role}; `/auth/me` → Principal) and the
 * UI spec (08-ui-spec). Paths marked (*) are per the contract and verified
 * against staging when the matching screen is wired.
 */
import { http } from './client';

export interface Principal {
  id: string;
  email: string;
  role: string;
  customRoleId: string | null;
  impersonating: boolean;
  /** spec-extended, optional until the API is confirmed to send them */
  displayName?: string;
  preferredLanguage?: 'cs-CZ' | 'en-US';
  workspaceIds?: string[];
}

/** Login either authenticates (session set via cookie) or demands a 2nd factor. */
export type LoginResult =
  | { mfaRequired: true; challengeId: string }
  | { mfaRequired?: false; userId: string; role: string; landing?: 'staff' | 'client' };

export function isMfaRequired(r: LoginResult): r is { mfaRequired: true; challengeId: string } {
  return 'mfaRequired' in r && r.mfaRequired === true;
}

export interface LoginInput {
  email: string;
  password: string;
}
export interface MfaLoginInput {
  challengeId: string;
  /** TOTP code or a backup code — exactly one. */
  code?: string;
  backupCode?: string;
}

export const iam = {
  /** POST /api/iam/auth/login → LoginResult (sets cookies on success). */
  login: (body: LoginInput) => http.post<LoginResult>('/api/iam/auth/login', body),
  /** POST /api/iam/auth/login/mfa → LoginResult (completes the MFA split). */
  loginMfa: (body: MfaLoginInput) => http.post<LoginResult>('/api/iam/auth/login/mfa', body),
  /** GET /api/iam/auth/me → the signed-in principal. */
  me: () => http.get<Principal>('/api/iam/auth/me'),
  /** POST /api/iam/auth/logout → clears cookies. */
  logout: () => http.post<void>('/api/iam/auth/logout'),
  /**
   * (*) POST /api/iam/password-reset/request → always neutral (no enumeration).
   *
   * NB the path is `iam/password-reset`, NOT `iam/auth/reset-password`. Both of these
   * pointed at `iam/auth/reset-password/*`, which no controller has ever served — so
   * every "Forgot password" click 404'd before it reached any of the logic below it.
   * PasswordResetsController is mounted at @Controller('iam/password-reset').
   */
  resetRequest: (body: { email: string }) =>
    http.post<void>('/api/iam/password-reset/request', body),
  /** (*) POST /api/iam/password-reset/confirm → 410 GoneError if token used/expired. */
  resetConfirm: (body: { token: string; newPassword: string }) =>
    http.post<void>('/api/iam/password-reset/confirm', body),
  /** (*) POST /api/iam/auth/set-initial-password → set credential from invite token. */
  setInitialPassword: (body: { token: string; newPassword: string }) =>
    http.post<void>('/api/iam/auth/set-initial-password', body),
};
